import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../../services/api';

export const Revenue: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revenueData, setRevenueData] = useState<{ month: string; revenue: number }[]>([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = (await api.getRevenueData()) || [];
      setRevenueData(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar ingresos');
      setRevenueData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalRevenue = revenueData.reduce((sum, item) => sum + item.revenue, 0);
  const averageRevenue = revenueData.length > 0 ? totalRevenue / revenueData.length : 0;
  const maxRevenue = revenueData.length > 0 ? Math.max(...revenueData.map((item) => item.revenue)) : 0;
  const minRevenue =
    revenueData.length > 0 ? Math.min(...revenueData.map((item) => item.revenue)) : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <DollarSign className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Ingresos Brutos</h1>
        </div>
        <div className="flex justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <DollarSign className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Ingresos Brutos</h1>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm font-medium"
        >
          Actualizar
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-sm text-gray-600 mb-1">Total (12 meses)</div>
          <div className="text-2xl font-bold text-primary">${Math.round(totalRevenue).toLocaleString()}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Promedio Mensual</div>
          <div className="text-2xl font-bold text-primary">${Math.round(averageRevenue).toLocaleString()}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Máximo Mensual</div>
          <div className="text-2xl font-bold text-green-600">${maxRevenue.toLocaleString()}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Mínimo Mensual</div>
          <div className="text-2xl font-bold text-red-600">${minRevenue.toLocaleString()}</div>
        </Card>
      </div>

      <Card title="Ingresos Mensuales (Últimos 12 Meses)">
        {revenueData.length === 0 ? (
          <p className="text-gray-500 py-8">No hay datos de ingresos.</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip
                formatter={(value: number) => [`$${Number(value).toLocaleString()}`, 'Ingresos']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="revenue" fill="#FA4999" name="Ingresos" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
};
